import { useState } from "react";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import GrTextField from "src/design-system/components/GrTextField";
import GrButton from "src/design-system/components/GrButton";
import GrIcon from "src/design-system/components/GrIcon";
import icons from "src/design-system/icons";
import { GrPasswordToggle } from "src/design-system/components/GrAuthLayout";
import { gr } from "src/design-system/tokens";

// Labels always above the fields: a browser that autofills on load does not tell the page until the
// field is clicked, so a floating label would sit on top of the saved email / password.
const LABEL_ABOVE = { shrink: true };

export default function LoginForm({ onhandleLogin, isLoading }) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    onhandleLogin(formData);
  };

  // Caps Lock is read from the key events of the password field itself (no window listeners).
  const handleCapsLock = (e) => {
    if (typeof e.getModifierState === "function") {
      setCapsLockOn(e.getModifierState("CapsLock"));
    }
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: gr.space[4] }}>
        <GrTextField
          InputLabelProps={LABEL_ABOVE}
          autoComplete="username"
          type="email"
          label="Email address"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <GrTextField
          InputLabelProps={LABEL_ABOVE}
          autoComplete="current-password"
          type={showPassword ? "text" : "password"}
          id="login-password"
          label="Password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleCapsLock}
          onKeyUp={handleCapsLock}
          onBlur={() => setCapsLockOn(false)}
          helperText={
            capsLockOn ? (
              <Box
                component="span"
                role="status"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: gr.space[1],
                  color: gr.color.late,
                }}
              >
                <GrIcon icon={icons.alert} size="sm" />
                Caps Lock is on
              </Box>
            ) : null
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <GrPasswordToggle
                  shown={showPassword}
                  onToggle={handleShowPassword}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: gr.space[2],
          mb: gr.space[6],
        }}
      >
        <GrButton
          variant="link"
          size="md"
          component={RouterLink}
          to="/forget_password"
        >
          Forgot password?
        </GrButton>
      </Box>
      <GrButton type="submit" size="lg" fullWidth loading={isLoading}>
        Login
      </GrButton>
    </form>
  );
}

LoginForm.propTypes = {
  onhandleLogin: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
